<template>
  <BaseDrawer
    v-model:visible="isNavVisible"
    position="left"
    class="navigation-drawer"
    @hide="hideDrawer"
  >
    <template #header>
      <div class="drawer__header" />
    </template>
    <div class="drawer__body">
      <div v-for="navItem in userNavInfo" :key="navItem.id" class="drawer__content">
        <RouterLink
          v-if="navItem.children.length == 0 && store.user?.is_authenticated"
          :id="navItem.id"
          :to="navItem.route"
          class="drawer__link"
          @click="hideDrawer"
        >
          {{ navItem.title }}
        </RouterLink>
        <button
          v-if="navItem.children.length > 0 && store.user?.is_authenticated"
          class="drawer__sub-menu-header"
          :class="isDrawerMenuVisible ? 'drawer__link-expand' : ''"
          @click="isDrawerMenuVisible = !isDrawerMenuVisible"
        >
          {{ navItem.title }}
          <div>
            <KIcon v-if="isDrawerMenuVisible" icon="fa-solid fa-caret-up" size="sm" />
            <KIcon v-else icon="fa-solid fa-caret-down" size="sm" />
          </div>
        </button>
        <ul
          v-if="navItem.children.length > 0 && store.user?.is_authenticated && isDrawerMenuVisible"
          class="drawer__sub-menu"
        >
          <li
            v-for="navChild in navItem.children"
            :key="navChild.class"
            class="drawer__sub-menu-content"
          >
            <button
              :id="navChild.class"
              class="drawer__sub-menu-content-link"
              @click="(event) => navigate(navChild.command, event)"
            >
              {{ navChild.label }}
            </button>
          </li>
        </ul>
      </div>
    </div>
  </BaseDrawer>
</template>

<script setup lang="ts">
import { KIcon } from "@k2united/frontend_components";
import BaseDrawer from "@/components/base/BaseDrawer.vue";
import { useUserStore } from "@/stores/user";
import { computed, ref } from "vue";

const store = useUserStore();

// Nav Buttons
const userNavInfo = computed(() => {
  if (store.isInternalAdmin) {
    return store.getInternalNavInfo;
  }
  return store.getNavInfo;
});

//Mobile Drawer
const isNavVisible = ref(false);
const isDrawerMenuVisible = ref(false);

const showDrawer = () => {
  isNavVisible.value = true;
};

const hideDrawer = () => {
  isNavVisible.value = false;
  isDrawerMenuVisible.value = false;
};

const navigate = (command: ((event: any) => void) | undefined, event: MouseEvent) => {
  if (command) {
    command(event);
  }
  hideDrawer();
};

defineExpose({
  hideDrawer,
  showDrawer
});
</script>

<style scoped lang="scss">
.drawer__body {
  margin-top: 0.5rem;
}

.drawer__content {
  padding: 0 1rem;
}

.drawer__sub-menu-content {
  background: linear-gradient(
      to right,
      $color-background-default 1rem,
      $color-border-table-default 0
    )
    bottom right no-repeat;
  background-size: 100% 1px;
  border-bottom: none;
  padding: 1rem 1.8rem;
  text-align: left;
  width: 100%;
}

.drawer__sub-menu-header {
  align-items: center;
  border-bottom: solid 1px $color-border-subtler;
  color: $color-text-brand;
  display: flex;
  font-weight: 600;
  justify-content: space-between;
  padding: 1.5rem 1rem;
  text-align: left;
  text-decoration: none;
  width: 100%;
}

.drawer__sub-menu {
  list-style: none;

  &-content,
  &-header {
    &:hover,
    &:focus-visible {
      background-color: $color-industry-blue-highlight;
    }
  }
}

.drawer__link {
  border-bottom: solid 1px $color-border-subtler;
  color: $color-text-brand;
  display: block;
  font-family: $font-family-button;
  font-weight: 600;
  padding: 1.5rem 1rem;
  text-decoration: none;
  width: 100%;
  &:focus-visible {
    box-shadow:
      0 0 0 2px #ffffff,
      0 0 0 4px $color-shadow-button-focused;
  }

  &:hover,
  &:focus {
    background-color: $color-industry-blue-highlight;
  }

  &.router-link-active {
    background-color: $color-industry-blue-subtlest;
  }
}

.drawer__link-expand {
  background-color: $color-industry-blue-highlight;
}

.drawer__sub-menu-content-link {
  color: $color-text-brand;
  font-weight: 600;
  text-align: left;
  width: 100%;
}

.navigation-drawer {
  width: 20rem;

  @include tablet {
    width: 26.25rem;
  }
}
</style>
